import { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = "vtp_survey_done";

export const SurveyOverlay = () => {
  const { t } = useLang();
  const [show, setShow] = useState(false);
  const [choice, setChoice] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (localStorage.getItem(KEY)) return;
    const onScroll = () => {
      const half = (document.documentElement.scrollHeight - window.innerHeight) * 0.5;
      if (window.scrollY >= half) {
        setShow(true);
        window.removeEventListener("scroll", onScroll);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const close = () => {
    localStorage.setItem(KEY, "1");
    setShow(false);
  };

  const send = async () => {
    if (!choice) return;
    setSending(true);
    try {
      await axios.post(`${API}/survey`, { channel: choice });
      toast.success(t.survey.thanks);
    } catch (e) {
      // silencieux : ne pas bloquer le visiteur
    } finally {
      setSending(false);
      close();
    }
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="fixed inset-0 z-[90] bg-white/60 backdrop-blur-[6px] flex items-center justify-center px-6"
          data-testid="survey-overlay"
        >
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15, ease: [0.19, 1, 0.22, 1] }}
            className="relative w-full max-w-md bg-sand-bg border border-[var(--line)] p-8 md:p-10 shadow-xl"
          >
            <button
              onClick={close}
              aria-label="Fermer"
              data-testid="survey-close-btn"
              className="absolute top-4 right-4 text-ink-soft hover:text-ink transition-colors"
            >
              <X className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <span className="text-[11px] tracking-[0.35em] uppercase text-terracotta">{t.survey.eyebrow}</span>
            <h3 className="font-serif font-light text-2xl md:text-3xl mt-3 mb-8 tracking-tight">{t.survey.question}</h3>
            <div className="space-y-4 mb-10">
              {t.survey.options.map((o) => (
                <button
                  key={o.key}
                  onClick={() => setChoice(o.key)}
                  data-testid={`survey-option-${o.key}`}
                  className="w-full flex items-center justify-between group"
                >
                  <span className={`text-base font-light transition-colors ${choice === o.key ? "text-ink" : "text-ink-soft group-hover:text-ink"}`}>
                    {o.label}
                  </span>
                  <span
                    className={`w-5 h-5 rounded-full border transition-all duration-300 ${
                      choice === o.key ? "border-terracotta bg-terracotta" : "border-ink/30 group-hover:border-ink"
                    }`}
                  />
                </button>
              ))}
            </div>
            <button
              onClick={send}
              disabled={!choice || sending}
              data-testid="survey-send-btn"
              className="w-full bg-ink text-sand-bg py-3.5 text-sm tracking-wide hover:bg-terracotta transition-colors duration-300 disabled:opacity-40"
            >
              {t.survey.send}
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
